// Receiving (spec/13 §6.4 Receive; `inventory:receive`, or `inventory:supplies` for supply items): a delivery is a
// supplier (the item's usual one, pre-selected), the supplier's invoice number and its lines — each an item (searched, or scanned: a case's barcode picks
// the case), how much in packs and loose units, and what the line cost, typed in rupees (as a line total or per pack)
// and turned into paise by string arithmetic, never a float. Continue shows the delivery to check before it is saved;
// saving raises the quantities and the average costs at once. One `Idempotency-Key` is kept for as long as the same
// delivery is being sent, so a retry is never a second delivery.
//
// Deliveries lists what was received, newest first; a row opens the delivery as it was received.

import type { CreateStockReceiptRequest, StockItem, StockItemPack, StockReceipt } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Input, SegmentedControl, SelectField, Table, TextField, useToast, type TableColumn } from '@clavian/ui-web';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Facts, Notice, TableSkeleton } from '../../ui';
import { majorToMinor } from '../money';
import { suppliesRule, type StockAccess } from './access';
import { useStockFormat, useStockUnits } from './format';
import { blankEntry, entryToWire, entryTotal, ItemPicker, onlyPackOf, QuantityFields, type QuantityEntry } from './parts';
import { useCreateReceipt, useStockItems, useStockReceipt, useStockReceipts, useStockSuppliers } from './queries';
import { StockEmpty, useWhen, type StockDialog } from './StockPage';
import { useProblem } from '../../api/use-problem';

const NO_SUPPLIER = 'none';

interface Line {
  readonly key: string;
  readonly item: StockItem;
  readonly entry: QuantityEntry;
  readonly costMode: 'total' | 'perPack';
  readonly cost: string;
}

let lineKey = 0;
const newLine = (item: StockItem, pack: StockItemPack | null): Line => ({ key: `line-${(lineKey += 1)}`, item, entry: blankEntry(item, pack), costMode: 'total', cost: '' });

/** What a line cost in paise: the total typed, or the price per pack times the packs. `null` while it is not valid. */
function lineCost(line: Line, currency: string): number | null {
  const typed = majorToMinor(line.cost, currency);
  if (typed === null) return null;
  if (line.costMode === 'total') return typed;
  const only = onlyPackOf(line.entry, line.item);
  return only === null ? null : typed * only.count;
}

export function ReceiveDialog({ dialog, access, onClose }: { readonly dialog: Extract<StockDialog, { kind: 'receive' }> | null; readonly access: StockAccess; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={dialog !== null} onOpenChange={(open) => (open ? undefined : onClose())} size="lg" dirty="auto" title={t('stock.receive.title')} description={t('stock.receive.hint')}>
      {dialog !== null ? <ReceiveFlow start={dialog} access={access} onDone={onClose} /> : null}
    </Dialog>
  );
}

function ReceiveFlow({ start, access, onDone }: { readonly start: Extract<StockDialog, { kind: 'receive' }>; readonly access: StockAccess; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const currency = venue.currency ?? 'INR';
  const stock = useStockFormat();
  const items = useStockItems(venue.organizationId, venue.id);
  const suppliers = useStockSuppliers(venue.organizationId, venue.id);
  const receive = useCreateReceipt(venue.organizationId, venue.id);
  const [step, setStep] = useState<'lines' | 'review'>('lines');
  // The item's usual supplier is pre-selected — from the item it was opened on, or the first line added.
  const [supplierId, setSupplierId] = useState(start.item?.supplierId ?? NO_SUPPLIER);
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<readonly Line[]>(() => (start.item === undefined ? [] : [newLine(start.item, start.pack ?? null)]));
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // One key for as long as the same delivery is sent: a retry after a dropped answer is the receipt already made.
  const attempt = useRef<{ body: string; key: string } | null>(null);

  const update = (key: string, patch: Partial<Line>) => setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const total = lines.reduce((sum, line) => sum + (lineCost(line, currency) ?? 0), 0);
  const supplier = (suppliers.data ?? []).find((one) => one.id === supplierId);

  function check(): boolean {
    const next: Record<string, string> = {};
    for (const line of lines) {
      const quantity = entryTotal(line.entry, line.item);
      if (quantity === null || quantity === 'invalid' || quantity <= 0) next[`${line.key}:qty`] = t('stock.receive.quantityRequired');
      if (line.cost.trim() === '' || majorToMinor(line.cost, currency) === null) next[`${line.key}:cost`] = t('stock.receive.costRequired');
      else if (line.costMode === 'perPack' && onlyPackOf(line.entry, line.item) === null) next[`${line.key}:cost`] = t('stock.receive.perPackNeedsPacks');
    }
    setErrors(next);
    setFormError(lines.length === 0 ? t('stock.receive.noLines') : null);
    return lines.length > 0 && Object.keys(next).length === 0;
  }

  function body(): CreateStockReceiptRequest {
    return {
      ...(supplier === undefined ? {} : { supplierId: supplier.id }),
      ...(reference.trim() === '' ? {} : { reference: reference.trim() }),
      lines: lines.map((line) => ({ stockItemId: line.item.id, ...entryToWire(line.entry, line.item), cost: { amount: lineCost(line, currency) ?? 0, currency } })),
    };
  }

  function confirm(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const request = body();
    const text = JSON.stringify(request);
    if (attempt.current?.body !== text) attempt.current = { body: text, key: crypto.randomUUID() };
    receive.mutate(
      { body: request, key: attempt.current.key },
      {
        onSuccess: (receipt) => {
          show({ tone: 'ok', title: receipt.supplierName === null ? t('stock.receive.done', { count: receipt.lines.length }) : t('stock.receive.doneFrom', { count: receipt.lines.length, supplier: receipt.supplierName }) });
          onDone();
        },
        onError: (failure) => setFormError(problem.text(failure, { fallback: t('stock.receive.refused') })),
      },
    );
  }

  if (step === 'review') {
    const columns: TableColumn<Line>[] = [
      { key: 'item', header: t('stock.column.item'), cell: (line) => line.item.name },
      {
        key: 'quantity',
        header: t('stock.receive.quantity'),
        numeric: true,
        cell: (line) => {
          const quantity = entryTotal(line.entry, line.item);
          return <span className="console-num">{typeof quantity === 'number' ? stock.quantity(quantity, line.item) : ''}</span>;
        },
      },
      { key: 'packs', header: t('stock.receive.packs'), cell: (line) => <span className="console-muted">{packWords(line, t)}</span> },
      { key: 'cost', header: t('stock.receive.lineCost'), numeric: true, cell: (line) => <span className="console-money">{stock.money({ amount: lineCost(line, currency) ?? 0, currency })}</span> },
    ];
    return (
      <form className="console-form" aria-label={t('stock.receive.reviewTitle')} noValidate onSubmit={confirm}>
        <h3 className="console-inline-step__title">{t('stock.receive.reviewTitle')}</h3>
        {formError !== null ? (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        ) : null}
        <Facts
          columns={3}
          items={[
            { key: 'supplier', label: t('stock.receive.supplier'), value: supplier?.name ?? t('stock.receive.noSupplier') },
            { key: 'reference', label: t('stock.receive.reference'), value: reference.trim() === '' ? t('stock.receive.noReference') : reference.trim() },
            { key: 'total', label: t('stock.receive.total'), value: <span className="console-money console-stock-total">{stock.money({ amount: total, currency })}</span> },
          ]}
        />
        <div className="console-table-card">
          <Table caption={t('stock.receive.reviewCaption')} captionHidden columns={columns} rows={lines} rowKey={(line) => line.key} />
        </div>
        <p className="console-subtle">{t('stock.receive.reviewNote')}</p>
        <div className="console-form__actions console-form__actions--end">
          <Button type="button" variant="ghost" onClick={() => setStep('lines')}>
            {t('common.back')}
          </Button>
          <Button type="submit" variant="primary" busy={receive.isPending}>
            {t('stock.receive.confirm', { amount: stock.money({ amount: total, currency }) })}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      className="console-form"
      aria-label={t('stock.receive.title')}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (check()) setStep('review');
      }}
    >
      {formError !== null ? (
        <Notice tone="danger" role="alert">
          {formError}
        </Notice>
      ) : null}
      <div className="console-form__row">
        <SelectField
          label={t('stock.receive.supplier')}
          value={supplierId}
          onValueChange={setSupplierId}
          options={[{ value: NO_SUPPLIER, label: t('stock.receive.noSupplier') }, ...(suppliers.data ?? []).map((one) => ({ value: one.id, label: one.name }))]}
        />
        <TextField
          label={t('stock.receive.reference')}
          showOptional
          autoComplete="off"
          spellCheck={false}
          translate="no"
          value={reference}
          placeholder={t('stock.receive.referencePlaceholder')}
          onChange={(event) => setReference(event.target.value)}
        />
      </div>
      <ItemPicker
        organizationId={venue.organizationId}
        venueId={venue.id}
        items={items.data ?? []}
        label={t('stock.receive.addItem')}
        exclude={new Set(lines.map((line) => line.item.id))}
        {...(access.receiveSuppliesOnly ? { allow: suppliesRule(true) } : {})}
        autoFocus={start.item === undefined}
        onPick={(item, pack) => {
          setLines((current) => [...current, newLine(item, pack)]);
          if (supplierId === NO_SUPPLIER && item.supplierId !== null) setSupplierId(item.supplierId);
        }}
      />
      {lines.length === 0 ? (
        <p className="console-stock-hint">{t('stock.receive.empty')}</p>
      ) : (
        <ol className="console-stock-lines" aria-label={t('stock.receive.linesLabel')}>
          {lines.map((line) => {
            const only = onlyPackOf(line.entry, line.item);
            const cost = lineCost(line, currency);
            return (
              <li key={line.key} className="console-stock-line">
                <div className="console-stock-line__head">
                  <span className="console-stock-line__name">{line.item.name}</span>
                  <span className="console-subtle">{t('stock.receive.onHandNow', { quantity: stock.quantity(line.item.onHand, line.item) })}</span>
                  <Button type="button" variant="ghost" size="sm" icon={<Icon name="close" />} label={t('stock.receive.removeLine', { name: line.item.name })} onClick={() => setLines((current) => current.filter((one) => one.key !== line.key))} />
                </div>
                <QuantityFields item={line.item} entry={line.entry} label={line.item.name} onChange={(entry) => update(line.key, { entry })} error={errors[`${line.key}:qty`]} />
                <div className="console-stock-line__cost">
                  <SegmentedControl<'total' | 'perPack'>
                    size="sm"
                    label={t('stock.receive.costMode', { name: line.item.name })}
                    value={line.costMode}
                    onValueChange={(costMode) => update(line.key, { costMode })}
                    options={[
                      { value: 'total', label: t('stock.receive.costTotal') },
                      { value: 'perPack', label: only === null ? t('stock.receive.costPerPack') : t('stock.receive.costPer', { pack: only.pack.name }), disabled: line.item.packs.length === 0 },
                    ]}
                  />
                  <label className="console-stock-money">
                    <span className="clav-visually-hidden">{t('stock.receive.costOf', { name: line.item.name, currency })}</span>
                    <span className="console-stock-money__symbol" aria-hidden="true">
                      {currency === 'INR' ? '₹' : currency}
                    </span>
                    <Input
                      numeric
                      inputMode="decimal"
                      className="console-stock-money__input"
                      value={line.cost}
                      placeholder="0.00"
                      aria-invalid={errors[`${line.key}:cost`] === undefined ? undefined : true}
                      onChange={(event) => update(line.key, { cost: event.target.value })}
                    />
                  </label>
                  {line.costMode === 'perPack' && only !== null && cost !== null ? (
                    <span className="console-subtle console-num">{t('stock.receive.perPackTotal', { count: only.count, total: stock.money({ amount: cost, currency }) })}</span>
                  ) : null}
                </div>
                {errors[`${line.key}:cost`] !== undefined ? (
                  <p className="clav-field__error" role="alert">
                    {errors[`${line.key}:cost`]}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      <div className="console-form__actions console-form__actions--end">
        <span className="console-stock-summary console-num" aria-live="polite">
          {t('stock.receive.summary', { count: lines.length, total: stock.money({ amount: total, currency }) })}
        </span>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('stock.receive.review')}
        </Button>
      </div>
    </form>
  );
}

/** "4 × 2.5 kg bag + 500 g loose", the packs as they were typed. */
function packWords(line: Line, t: ReturnType<typeof useTranslation>['t']): string {
  const parts = line.item.packs
    .filter((pack) => (line.entry.packs[pack.id] ?? '').trim() !== '' && Number(line.entry.packs[pack.id]) > 0)
    .map((pack) => t('stock.receive.packTimes', { count: Number(line.entry.packs[pack.id]), pack: pack.name }));
  if (line.entry.loose.trim() !== '') parts.push(t('stock.receive.looseWords', { amount: line.entry.loose.trim(), unit: t(`stock.unitName.${line.entry.unit}`) }));
  return parts.join(' + ');
}

// -------------------------------------------------------------------------------------------------
// Deliveries
// -------------------------------------------------------------------------------------------------

export function DeliveriesTab({
  access,
  receiptId,
  onOpenReceipt,
  onReceive,
}: {
  readonly access: StockAccess;
  readonly receiptId: string | null;
  readonly onOpenReceipt: (receiptId: string | null) => void;
  readonly onReceive: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const stock = useStockFormat();
  const when = useWhen();
  const receipts = useStockReceipts(venue.organizationId, venue.id);
  const rows = (receipts.data?.pages ?? []).flatMap((page) => page.data);

  const columns: TableColumn<StockReceipt>[] = [
    {
      key: 'received',
      header: t('stock.deliveries.received'),
      cell: (receipt) => (
        <button type="button" className="console-cell-link" onClick={() => onOpenReceipt(receipt.id)}>
          <span className="console-num">{when(receipt.receivedAt)}</span>
          <span className="console-cell-sub">{receipt.reference ?? t('stock.receive.noReference')}</span>
        </button>
      ),
    },
    { key: 'supplier', header: t('stock.receive.supplier'), cell: (receipt) => receipt.supplierName ?? <span className="console-muted">{t('stock.receive.noSupplier')}</span> },
    { key: 'items', header: t('stock.deliveries.items'), cell: (receipt) => <span className="console-muted">{receipt.lines.map((line) => line.name).join(', ')}</span> },
    { key: 'cost', header: t('stock.deliveries.cost'), numeric: true, cell: (receipt) => <span className="console-money">{stock.money(receipt.totalCost)}</span> },
    { key: 'by', header: t('stock.deliveries.by'), cell: (receipt) => <span className="console-muted">{receipt.recordedByName ?? t('stock.history.system')}</span> },
  ];

  let body: ReactNode;
  if (receipts.data === undefined) {
    body = receipts.isError ? <LoadFailure error={receipts.error} message={t('stock.deliveries.unavailable')} onRetry={() => void receipts.refetch()} /> : <TableSkeleton />;
  } else if (rows.length === 0) {
    body = (
      <StockEmpty
        title={t('stock.deliveries.emptyTitle')}
        description={t('stock.deliveries.empty')}
        action={
          access.receive ? (
            <Button variant="primary" onClick={onReceive}>
              {t('stock.actions.receive')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <>
        <div className="console-table-card">
          <Table caption={t('stock.deliveries.caption')} captionHidden columns={columns} rows={rows} rowKey={(receipt) => receipt.id} />
        </div>
        {receipts.hasNextPage ? (
          <div className="console-list-foot">
            <Button variant="secondary" size="sm" busy={receipts.isFetchingNextPage} onClick={() => void receipts.fetchNextPage()}>
              {t('stock.history.more')}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="console-stock-tab">
      {body}
      <ReceiptDialog receiptId={receiptId} onClose={() => onOpenReceipt(null)} />
    </div>
  );
}

/** One delivery as it was received: its supplier, reference, lines and total. */
function ReceiptDialog({ receiptId, onClose }: { readonly receiptId: string | null; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const stock = useStockFormat();
  const when = useWhen();
  const receipt = useStockReceipt(venue.organizationId, venue.id, receiptId);
  const units = useStockUnits(receiptId !== null);
  const data = receipt.data;
  type ReceiptLine = StockReceipt['lines'][number];
  const columns: TableColumn<ReceiptLine>[] = [
    { key: 'item', header: t('stock.column.item'), cell: (line) => line.name },
    { key: 'quantity', header: t('stock.receive.quantity'), numeric: true, cell: (line) => <span className="console-num">{stock.quantity(line.quantity, units(line.stockItemId, line.baseUnit))}</span> },
    {
      key: 'packs',
      header: t('stock.receive.packs'),
      cell: (line) => (
        <span className="console-muted">
          {[
            ...line.packs.filter((pack) => pack.count > 0).map((pack) => t('stock.receive.packTimes', { count: pack.count, pack: pack.name })),
            ...(line.looseQuantity > 0 ? [t('stock.receive.looseOf', { quantity: stock.quantity(line.looseQuantity, units(line.stockItemId, line.baseUnit)) })] : []),
          ].join(' + ')}
        </span>
      ),
    },
    { key: 'cost', header: t('stock.receive.lineCost'), numeric: true, cell: (line) => <span className="console-money">{stock.money(line.cost)}</span> },
  ];
  return (
    <Dialog
      open={receiptId !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="lg"
      eyebrow={t('stock.deliveries.eyebrow')}
      title={data === undefined ? t('stock.detail.loading') : (data.supplierName ?? t('stock.receive.noSupplier'))}
      description={data === undefined ? undefined : t('stock.deliveries.receivedBy', { when: when(data.receivedAt), who: data.recordedByName ?? t('stock.history.system') })}
    >
      {data === undefined ? (
        receipt.isError ? (
          <LoadFailure error={receipt.error} message={t('stock.deliveries.unavailable')} onRetry={() => void receipt.refetch()} />
        ) : (
          <TableSkeleton rows={4} />
        )
      ) : (
        <div className="console-form">
          <Facts
            columns={3}
            items={[
              { key: 'reference', label: t('stock.receive.reference'), value: data.reference ?? t('stock.receive.noReference') },
              { key: 'lines', label: t('stock.deliveries.items'), value: <span className="console-num">{data.lines.length}</span> },
              { key: 'total', label: t('stock.receive.total'), value: <span className="console-money console-stock-total">{stock.money(data.totalCost)}</span> },
            ]}
          />
          <div className="console-table-card">
            <Table caption={t('stock.receive.reviewCaption')} captionHidden columns={columns} rows={data.lines} rowKey={(line) => line.stockItemId} />
          </div>
          {data.note === null ? null : <p className="console-muted">{data.note}</p>}
          <p className="console-subtle">{t('stock.deliveries.immutable')}</p>
        </div>
      )}
    </Dialog>
  );
}
